import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Alert } from '@//components/Alert';
import { Badge } from '@//components/Badge';
import { RangeSlider, type SingleRange } from '@//components/RangeSlider';
import type { GalleryImage } from '../utils';
import styles from './GalleryModal360.module.scss';

interface GalleryModal360Props {
  id: string;
  images: GalleryImage[];
}

const GalleryModal360: React.FC<GalleryModal360Props> = ({ id, images }) => {
  const t = useTranslations('pages.item.imageGallery.360Viewer');

  // Calculate starting position based on odd/even number of images
  const getStartingPosition = () => {
    if (images.length % 2 === 0) {
      // Even: start at middle, showing image 2 (index 1)
      return images.length + 1;
    }
    // Odd: start at middle, showing image 1 (index 0)
    return images.length;
  };

  const [current, setCurrent] = useState(getStartingPosition());
  const [hasError, setHasError] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const dragging = useRef(false);
  const lastX = useRef<number | null>(null);
  const galleryRef = useRef<HTMLDivElement>(null);

  // Announce changes to screen readers
  const announceChange = (message: string) => {
    setAnnouncement(message);
    // Clear announcement after a short delay
    setTimeout(() => setAnnouncement(''), 1000);
  };

  const handleImageError = () => {
    setHasError(true);
  };

  // Drag logic
  const onStart = (clientX: number) => {
    dragging.current = true;
    lastX.current = clientX;
    // Announce drag start for screen readers
    announceChange(t('announcements.dragStarted'));
  };

  const onMove = (clientX: number) => {
    if (!dragging.current || lastX.current == null) return;
    const dx = clientX - lastX.current;
    if (Math.abs(dx) > 40) {
      const direction = dx > 0 ? 1 : -1; // Inverted: drag right = +1, drag left = -1
      const newPosition = current + direction;
      // Allow rotation within the full range (0 to 2 * images.length)
      if (newPosition >= 0 && newPosition <= 2 * images.length) {
        setCurrent(newPosition);
        // Announce position change
        const imageIndex = getImageIndex(newPosition);
        announceChange(
          t('announcements.positionChanged', {
            frame: imageIndex + 1,
            total: images.length,
          }),
        );
      }
      lastX.current = clientX;
    }
  };

  const onEnd = () => {
    if (dragging.current) {
      dragging.current = false;
      lastX.current = null;
      announceChange(t('announcements.dragEnded'));
    }
  };

  // Handlers
  const handlePointerDown = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if ('touches' in e && e.touches.length) {
      onStart(e.touches[0].clientX);
    } else if ('clientX' in e) {
      onStart(e.clientX);
    }
  };

  const handlePointerMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!dragging.current) return;
    e.preventDefault();
    if ('touches' in e && e.touches.length) {
      onMove(e.touches[0].clientX);
    } else if ('clientX' in e) {
      onMove(e.clientX);
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => e.preventDefault();

  // Enhanced keyboard navigation for accessibility
  const handleKeyDown = (e: React.KeyboardEvent) => {
    let newPosition = current;

    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        newPosition = current + 1;
        if (newPosition <= 2 * images.length) {
          setCurrent(newPosition);
          const imageIndex = getImageIndex(newPosition);
          announceChange(
            t('announcements.positionChanged', {
              frame: imageIndex + 1,
              total: images.length,
            }),
          );
        }
        e.preventDefault();
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        newPosition = current - 1;
        if (newPosition >= 0) {
          setCurrent(newPosition);
          const imageIndex = getImageIndex(newPosition);
          announceChange(
            t('announcements.positionChanged', {
              frame: imageIndex + 1,
              total: images.length,
            }),
          );
        }
        e.preventDefault();
        break;
      case 'Home': {
        newPosition = 0;
        setCurrent(newPosition);
        const homeImageIndex = getImageIndex(newPosition);
        announceChange(
          t('announcements.positionChanged', {
            frame: homeImageIndex + 1,
            total: images.length,
          }),
        );
        e.preventDefault();
        break;
      }
      case 'End': {
        newPosition = 2 * images.length;
        setCurrent(newPosition);
        const endImageIndex = getImageIndex(newPosition);
        announceChange(
          t('announcements.positionChanged', {
            frame: endImageIndex + 1,
            total: images.length,
          }),
        );
        e.preventDefault();
        break;
      }
      case 'PageUp': {
        newPosition = Math.max(0, current - Math.ceil(images.length / 4));
        setCurrent(newPosition);
        const pageUpImageIndex = getImageIndex(newPosition);
        announceChange(
          t('announcements.positionChanged', {
            frame: pageUpImageIndex + 1,
            total: images.length,
          }),
        );
        e.preventDefault();
        break;
      }
      case 'PageDown': {
        newPosition = Math.min(2 * images.length, current + Math.ceil(images.length / 4));
        setCurrent(newPosition);
        const pageDownImageIndex = getImageIndex(newPosition);
        announceChange(
          t('announcements.positionChanged', {
            frame: pageDownImageIndex + 1,
            total: images.length,
          }),
        );
        e.preventDefault();
        break;
      }
    }
  };

  // Handle slider change
  const handleSliderChange = (value: SingleRange) => {
    const newPosition = value[0];
    setCurrent(newPosition);
    const imageIndex = getImageIndex(newPosition);
    announceChange(
      t('announcements.positionChanged', {
        frame: imageIndex + 1,
        total: images.length,
      }),
    );
  };

  // Focus management
  useEffect(() => {
    if (galleryRef.current) {
      galleryRef.current.focus();
    }
  }, []);

  if (!images?.length) {
    return (
      <Alert id={`${id}-alert-no-images`} variant="info">
        {t('noImages')}
      </Alert>
    );
  }

  // Map the slider position to image index
  const getImageIndex = (position: number) => {
    // Center is at position = images.length, showing image 1 (index 0)
    // Moving left from center should show: 6,5,4,3,2,1
    // Moving right from center should show: 1,2,3,4,5,6
    const offset = position - images.length;

    if (offset < 0) {
      // Left side: going backwards
      // offset -1 -> image 6 (index 5)
      // offset -2 -> image 5 (index 4)
      return (images.length + offset) % images.length;
    }
    // Center and right side: going forwards
    // offset 0 -> image 1 (index 0)
    // offset 1 -> image 2 (index 1)
    return offset % images.length;
  };

  const currentImageIndex = getImageIndex(current);

  if (hasError) {
    return (
      <Alert id={`${id}-alert-error`} variant="critical" heading={t('error.heading')}>
        {t('error.description')}
      </Alert>
    );
  }

  return (
    <figure
      aria-label={t('ariaLabel')}
      aria-describedby={`${id}-viewer-description`}
      className={styles.figure}
    >
      <div id={`${id}-viewer-description`} className="sr-only">
        {t('ariaDescription', { total: images.length })}
      </div>
      <div
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        aria-label={t('announcements.ariaLabel')}
      >
        {announcement}
      </div>

      <Badge tone="brand" size="sm" className={styles.badge} aria-hidden="true">
        {t('badge')}
      </Badge>

      <div
        ref={galleryRef}
        role="button"
        tabIndex={0}
        aria-label={t('galleryAriaLabel')}
        aria-describedby={`${id}-controls-description`}
        onKeyDown={handleKeyDown}
        onMouseDown={handlePointerDown}
        onMouseMove={handlePointerMove}
        onMouseUp={onEnd}
        onMouseLeave={onEnd}
        onTouchStart={handlePointerDown}
        onTouchMove={handlePointerMove}
        onTouchEnd={onEnd}
        onTouchCancel={onEnd}
        onContextMenu={handleContextMenu}
        className={styles.gallery}
        aria-pressed={false}
      >
        <div id={`${id}-controls-description`} className="sr-only">
          {t('galleryAriaDescription')}
        </div>

        {images.map((image, index) => {
          const isVisible = index === currentImageIndex;

          return (
            <img
              key={`${image.src}-${index}`}
              id={`${id}-image-${index}`}
              src={image.src}
              srcSet={image.srcSet}
              alt={t('imageAlt', {
                frame: index + 1,
                total: images.length,
                name: image.alt || t('imageAltFallbackName'),
              })}
              width={image.width}
              height={image.height}
              draggable={false}
              className={clsx(styles.image, isVisible && styles.imageVisible)}
              onError={handleImageError}
              aria-hidden={!isVisible}
              loading={index === 0 ? 'eager' : 'lazy'}
            />
          );
        })}
      </div>

      <div className={styles.slider}>
        <RangeSlider
          id={`${id}-slider`}
          label={t('slider.label')}
          min={0}
          max={2 * images.length}
          value={[current] as SingleRange}
          onChange={handleSliderChange}
          disabled={hasError}
          hideRange={true}
        />
        <span className={styles.sliderValue}>
          {t('slider.value', {
            frame: currentImageIndex + 1,
            total: images.length,
          })}
        </span>
      </div>
    </figure>
  );
};

export default GalleryModal360;
